/**
 * Контракт «Показатели качества теста» (остров `TestQualityModal`). Полный
 * контракт и решения — `api/docs/test/RFC-test-quality.md` §5. Тексты и уровни
 * бэкенд отдаёт уже переведёнными, клиент ничего не считает по порогам;
 * `domain`/`segments` из ответа не используются.
 *
 * Эндпоинт — `src/api/apiConfig.ts`:
 *   testQuality → GET /api/tests/{id}/quality
 *
 * === Контракт ответа (сокращённо) ===
 *
 * ```json
 * {
 *   "data": {
 *     "testId": 1692,
 *     "checkedAt": "2022-02-26",
 *     "checkedAtText": "26.02.2022",
 *     "sample": {"lang": "ru", "n": 14355, "isOwnLanguage": true},
 *     "levels": [{"level": "bad", "label": "плохо"}, …],
 *     "scales": [{
 *       "id": 4255, "name": "Позитивность", "n": 14355, "nText": "14 355", "sampleText": "14 355 человек",
 *       "indicators": [{"code": "alpha", "label": "Альфа Кронбаха", "description": "…",
 *         "value": 0.77, "valueText": "0,77", "level": "good", "levelLabel": "хорошо",
 *         "domain": [0, 1], "segments": [{"from": 0, "to": 0.6, "level": "bad"}, …]}],
 *       "validity": [{"value": 0.21, "valueText": "0,21", "level": "bad", "levelLabel": "не совпало с ожиданием",
 *         "expectation": "positive", "expectationText": "прямая связь — не совпало", "confirmed": false,
 *         "domain": [-1, 1], "refTestName": "Большая пятерка",
 *         "refTestUrl": "https://…/", "refScaleName": "Нейротизм (N)", "n": 559, "sampleText": "559 человек"}],
 *       "validitySummary": {"confirmed": 6, "total": 8, "required": 6, "valid": true,
 *         "text": "Валидность подтверждена: совпало с ожиданием 6 из 8"}
 *     }],
 *     "texts": {"title": "…", "checkedAt": "…", "sampleSize": "…", "wholeTest": "…",
 *       "validityTitle": "…", "validityDescription": "…", "scaleLabel": "…", "testLabel": "…",
 *       "sampleLabel": "…", "sampleNote": null}
 *   }
 * }
 * ```
 *
 * Бэкенд меняет имя поля или формат — правим этот файл (типы + нормализатор),
 * слайс, селекторы и компоненты поедут автоматически.
 */

/** Уровень качества значения: от худшего к лучшему. */
export type QualityLevel = 'bad' | 'medium' | 'good' | 'excellent';

export const QUALITY_LEVELS: readonly QualityLevel[] = ['bad', 'medium', 'good', 'excellent'];

/** Отрезок шкалы показателя `[from, to)` с уровнем (wire-shape). */
export interface QualitySegmentWire {
	from: number;
	to: number;
	level: string;
}

interface QualityValueWire {
	value: number;
	valueText: string;
	level: string | null;
	levelLabel: string | null;
	domain?: [number, number] | number[] | null;
	segments?: QualitySegmentWire[] | null;
}

export interface QualityIndicatorWire extends QualityValueWire {
	code: string;
	label: string;
	description?: string | null;
}

export interface QualityValidityWire extends QualityValueWire {
	refTestName?: string | null;
	refTestUrl?: string | null;
	refScaleName?: string | null;
	n?: number | null;
	sampleText?: string | null;
	/** Гипотеза методиста: positive | negative | none; уровень тогда — совпало (good) / нет (bad), без гипотезы — null. */
	expectation?: string | null;
	/** «прямая связь — совпало». */
	expectationText?: string | null;
	confirmed?: boolean | null;
}

export interface QualityScaleWire {
	id: number | null;
	name: string;
	n?: number | null;
	/** null у n и «более 20 000» здесь — выборка больше порога не раскрывается. */
	nText?: string | null;
	sampleText?: string | null;
	indicators?: QualityIndicatorWire[] | null;
	validity?: QualityValidityWire[] | null;
	validitySummary?: { confirmed: number; total: number; required: number; valid: boolean; text: string } | null;
}

export interface QualityTextsWire {
	title?: string | null;
	checkedAt?: string | null;
	sampleSize?: string | null;
	wholeTest?: string | null;
	validityTitle?: string | null;
	validityDescription?: string | null;
	scaleLabel?: string | null;
	testLabel?: string | null;
	sampleLabel?: string | null;
	expectationLabel?: string | null;
	sampleNote?: string | null;
}

/** Wire-shape ответа `GET /api/tests/{id}/quality` (поле `data`). */
export interface TestQualityWire {
	testId: number;
	checkedAt?: string | null;
	checkedAtText?: string | null;
	sample?: { lang?: string | null; n?: number | null; isOwnLanguage?: boolean } | null;
	levels?: Array<{ level: string; label: string }> | null;
	scales?: QualityScaleWire[] | null;
	texts?: QualityTextsWire | null;
}

export interface QualityValue {
	valueText: string;
	/** null — у корреляции нет гипотезы методиста, оценивать нечем: плашка нейтральная. */
	level: QualityLevel | null;
	levelLabel: string | null;
	/** 0..100, середина сегмента уровня. */
	pointerPercent: number | null;
}

export interface QualityIndicator extends QualityValue {
	code: string;
	label: string;
	description: string;
}

export interface QualityValidityItem extends QualityValue {
	refTestName: string | null;
	refTestUrl: string | null;
	refScaleName: string | null;
	sampleText: string | null;
	expectationText: string | null;
}

export interface QualityScale {
	/** Ключ для React: id шкалы или `whole` для показателей всего теста. */
	key: string;
	name: string;
	/** Размер выборки, отформатированный под язык страницы («14 355»). */
	sampleSizeText: string | null;
	indicators: QualityIndicator[];
	validity: QualityValidityItem[];
	/** «Валидность подтверждена: совпало с ожиданием 6 из 8» — есть, если методист задал гипотезы. */
	validitySummaryText: string | null;
}

export interface QualityTexts {
	title: string;
	checkedAt: string;
	sampleSize: string;
	validityTitle: string;
	validityDescription: string;
	scaleLabel: string;
	testLabel: string;
	sampleLabel: string;
	expectationLabel: string;
	sampleNote: string | null;
}

export interface TestQualityData {
	testId: number;
	checkedAtText: string | null;
	levels: Array<{ level: QualityLevel; label: string }>;
	scales: QualityScale[];
	texts: QualityTexts;
}

export interface TestQualityState {
	/** Для какого теста лежат данные — повторное открытие попапа не перезапрашивает. */
	testId: number | null;
	data: TestQualityData | null;
	isFetching: boolean;
	error: string | null;
	isNetworkError: boolean;
	/** Бэкенд ответил 404 — показателей у теста нет (или фича выключена). */
	isNotFound: boolean;
}

const toLevel = (raw: string): QualityLevel =>
	(QUALITY_LEVELS as readonly string[]).includes(raw) ? (raw as QualityLevel) : 'bad';

/**
 * Хвостик показывает уровень, а не точное значение: у межэлементной корреляции,
 * сложности и валидности шкала по значению немонотонна.
 */
export const getLevelPointerPercent = (level: QualityLevel): number =>
	((QUALITY_LEVELS.indexOf(level) + 0.5) / QUALITY_LEVELS.length) * 100;

const normalizeValue = (wire: QualityValueWire): QualityValue => {
	const level = wire.level ? toLevel(wire.level) : null;
	return {
		valueText: wire.valueText,
		level,
		levelLabel: wire.levelLabel ?? null,
		pointerPercent: level ? getLevelPointerPercent(level) : null,
	};
};

const formatCount = (n: number | null | undefined, locale: string): string | null => {
	if (typeof n !== 'number' || !Number.isFinite(n)) return null;
	try {
		// Латинские цифры, как у бэкенда (TestQualityPresenter, @numbers=latn): для ar
		// CLDR по умолчанию даёт арабско-индийские («١٤٬٣٥٥»).
		return new Intl.NumberFormat(locale, { numberingSystem: 'latn' }).format(n);
	} catch {
		return String(n);
	}
};

/** `locale` нужен только для разрядов размера выборки: остальные числа бэкенд уже отформатировал. */
export const normalizeTestQuality = (wire: TestQualityWire, locale: string): TestQualityData => {
	const texts = wire.texts ?? {};
	const wholeTest = texts.wholeTest ?? '';

	return {
		testId: wire.testId,
		checkedAtText: wire.checkedAtText ?? null,
		levels: (wire.levels ?? []).map((item) => ({ level: toLevel(item.level), label: item.label })),
		scales: (wire.scales ?? [])
			.map((scale): QualityScale => ({
				key: scale.id === null ? 'whole' : String(scale.id),
				name: scale.name || wholeTest,
				sampleSizeText: scale.nText ?? formatCount(scale.n, locale),
				indicators: (scale.indicators ?? []).map((indicator) => ({
					...normalizeValue(indicator),
					code: indicator.code,
					label: indicator.label,
					description: indicator.description ?? '',
				})),
				validity: (scale.validity ?? []).map((item) => ({
					...normalizeValue(item),
					refTestName: item.refTestName ?? null,
					refTestUrl: item.refTestUrl ?? null,
					refScaleName: item.refScaleName ?? null,
					sampleText: item.sampleText ?? null,
					expectationText: item.expectationText ?? null,
				})),
				validitySummaryText: scale.validitySummary?.text ?? null,
			}))
			.filter((scale) => scale.indicators.length > 0 || scale.validity.length > 0),
		texts: {
			title: texts.title ?? '',
			checkedAt: texts.checkedAt ?? '',
			sampleSize: texts.sampleSize ?? '',
			validityTitle: texts.validityTitle ?? '',
			validityDescription: texts.validityDescription ?? '',
			scaleLabel: texts.scaleLabel ?? '',
			testLabel: texts.testLabel ?? '',
			sampleLabel: texts.sampleLabel ?? '',
			expectationLabel: texts.expectationLabel ?? '',
			sampleNote: texts.sampleNote ?? null,
		},
	};
};
